import React, { useState, useEffect, useRef } from "react";
import CheckoutButton from "./CheckoutButton";

// Define types for our command sequence items
type ResultItem = {
  id: number;
  satellite: string;
  time: string;
  magnitude: string;
  weather: string;
  highlight: boolean;
};

type CommandSequenceItem =
  | { type: "command"; content: string }
  | { type: "system"; content: string }
  | { type: "success"; content: string }
  | { type: "result"; content: ResultItem[] };

const HowItWorks: React.FC = () => {
  const [isTyping, setIsTyping] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const terminalBodyRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState(0);
  const [stars, setStars] = useState<
    { x: number; y: number; size: number; opacity: number }[]
  >([]);

  // Steps information
  const howItWorksSteps = [
    {
      number: "01",
      title: "Enter Your Location",
      description:
        "Sign up with your city and email for personalized satellite tracking and predictions.",
      icon: "🌎",
    },
    {
      number: "02",
      title: "We Track Satellites",
      description:
        "Our system monitors 50+ satellites using real-time orbital data from NORAD.",
      icon: "🛰️",
    },
    {
      number: "03",
      title: "Weather Analysis",
      description:
        "We analyze local weather forecasts to ensure clear viewing conditions for optimal visibility.",
      icon: "🌤️",
    },
    {
      number: "04",
      title: "Get SpacePinged",
      description:
        "Receive detailed alerts with exact viewing times, directions, and photography tips.",
      icon: "📱",
    },
  ];

  // Command sequence to simulate typing
  const commandSequence: CommandSequenceItem[] = [
    {
      type: "command",
      content:
        "spaceping track --location=&quot;Chicago&quot; --date=&quot;2025-03-12&quot;",
    },
    { type: "system", content: "Initializing satellite tracker..." },
    { type: "system", content: "Accessing NORAD TLE database..." },
    { type: "system", content: "Checking weather conditions for Chicago..." },
    { type: "success", content: "Found 3 visible passes for tonight:" },
    {
      type: "result",
      content: [
        {
          id: 1,
          satellite: "STARLINK-4432",
          time: "20:15:42 CST",
          magnitude: "3.2",
          weather: "CLEAR SKIES",
          highlight: false,
        },
        {
          id: 2,
          satellite: "ISS",
          time: "19:22:15 CST",
          magnitude: "-2.5",
          weather: "CLEAR SKIES",
          highlight: true,
        },
        {
          id: 3,
          satellite: "ATLAS CENTAUR RB",
          time: "22:45:33 CST",
          magnitude: "4.2",
          weather: "CLEAR SKIES",
          highlight: false,
        },
      ],
    },
    {
      type: "command",
      content: "spaceping snapshot-score --target=&quot;STARLINK-4432&quot;",
    },
    { type: "success", content: "Snapshot Score: 8/10 - PRIME SHOT!" },
  ];

  // Generate stars for cosmic background
  useEffect(() => {
    const generatedStars = [];
    for (let i = 0; i < 150; i++) {
      generatedStars.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 0.5 + Math.random() * 2,
        opacity: 0.4 + Math.random() * 0.6,
      });
    }
    setStars(generatedStars);
  }, []);

  // Simulate terminal typing effect
  useEffect(() => {
    if (currentStep < commandSequence.length) {
      const timer = setTimeout(
        () => {
          setCurrentStep((prev) => prev + 1);

          // Scroll to bottom of terminal
          if (terminalBodyRef.current) {
            terminalBodyRef.current.scrollTop =
              terminalBodyRef.current.scrollHeight;
          }
        },
        currentStep === 0 ? 500 : 1000,
      ); // First command appears faster

      return () => clearTimeout(timer);
    } else {
      setIsTyping(false);
    }
  }, [currentStep, commandSequence.length]);

  // Change active section on interval for animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSection((prev) => (prev + 1) % howItWorksSteps.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [howItWorksSteps.length]);

  // Helper to render a command line with proper typing
  const renderCommandLine = (content: string, index: number) => {
    const parts = content.split(" ");
    const command = parts[0];
    const params = parts.slice(1);

    return (
      <div key={index} className="terminal-line">
        <span className="prompt">spaceping$</span>
        <span className="command">{command}</span>
        {params.map((param, i) => (
          <span key={i} className="param">
            {" "}
            {param}
          </span>
        ))}
      </div>
    );
  };

  const generateOrbitalPaths = () => {
    const paths = [];
    
    for (let i = 0; i < 3; i++) {
      const radius = 15 + i * 12;
      const rotationAngle = Math.random() * 40;
      const tilt = 0.5 + Math.random() * 0.3;

      paths.push({ radius, rotationAngle, tilt });
    }

    return paths;
  };

  const orbitalPaths = generateOrbitalPaths();

  return (
    <section
      className="how-it-works-section"
      id="how-it-works"
      ref={sectionRef}
    >
      {/* Cosmic background */}
      <div className="cosmic-background">
        <div className="stars-layer">
          {stars.map((star, index) => (
            <div
              key={index}
              className="star"
              style={{
                top: `${star.y}%`,
                left: `${star.x}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                boxShadow: `0 0 ${star.size * 2}px rgba(255, 255, 255, ${star.opacity})`,
              }}
            />
          ))}
        </div>

        <div className="shooting-stars">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="shooting-star"
              style={{
                top: `${Math.random() * 60}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 5 + Math.random() * 15}s`,
              }}
            ></div>
          ))}
        </div>

        <div className="cosmic-dust"></div>

        <div className="orbital-system">
          {orbitalPaths.map((path, index) => (
            <div
              key={index}
              className="orbital-path"
              style={{
                width: `${path.radius * 2}%`,
                height: `${path.radius * 2 * path.tilt}%`,
                transform: `translate(-50%, -50%) rotate(${path.rotationAngle}deg)`,
              }}
            >
              <div
                className="orbital-satellite"
                style={{
                  animationDuration: `${20 + index * 10}s`,
                  animationDelay: `${index * 3}s`,
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>

      <div className="container how-it-works-container">
        <div className="section-header">
          <h2 className="cosmic-title">
            <span className="title-highlight">How</span> It{" "}
            <span className="title-glow">Works</span>
          </h2>
          <p className="cosmic-subtitle">
            A step-by-step journey to tracking the cosmos above you
          </p>
        </div>

        <div className="how-it-works-content">
          {/* Terminal visualization side */}
          <div className="terminal-visualization">
            <div className="terminal-container">
              <div className="terminal-header">
                <div className="terminal-buttons">
                  <span className="terminal-button red"></span>
                  <span className="terminal-button yellow"></span>
                  <span className="terminal-button green"></span>
                </div>
                <span className="terminal-title">
                  mission-control — spacecraft-tracker
                </span>
                <div className="terminal-status">
                  <div className="status-indicator online"></div>
                  <span>LIVE</span>
                </div>
              </div>

              <div className="terminal-body" ref={terminalBodyRef}>
                {commandSequence.slice(0, currentStep).map((step, index) => {
                  if (step.type === "command") {
                    return renderCommandLine(step.content, index);
                  } else if (step.type === "system") {
                    return (
                      <div key={index} className="terminal-line">
                        <span className="system">{step.content}</span>
                      </div>
                    );
                  } else if (step.type === "success") {
                    return (
                      <div key={index} className="terminal-line">
                        <span className="success">{step.content}</span>
                      </div>
                    );
                  } else if (step.type === "result") {
                    return (
                      <div key={index} className="terminal-result">
                        {step.content.map((result) => (
                          <div
                            key={result.id}
                            className={`result-item ${result.highlight ? "highlight" : ""}`}
                          >
                            <span className="result-num">[{result.id}]</span>
                            <span className="result-sat">
                              {result.satellite}
                            </span>
                            <span className="result-time">{result.time}</span>
                            <span className="result-mag">
                              MAG: {result.magnitude}
                            </span>
                            <span className="result-weather">
                              {result.weather}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })}

                {isTyping && <div className="terminal-cursor"></div>}
              </div>
            </div>

            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon">🔭</div>
                <h3>Precision Tracking</h3>
                <p>Real-time satellite positions using NORAD TLE data</p>
              </div>
              <div className="info-card">
                <div className="info-icon">📸</div>
                <h3>Snapshot Score™</h3>
                <p>Photography quality rating from 1-10</p>
              </div>
            </div>
          </div>

          {/* Steps visualization side */}
          <div className="steps-visualization">
            {howItWorksSteps.map((step, index) => (
              <div
                key={index}
                className={`step-item ${activeSection === index ? "active" : ""}`}
                onClick={() => setActiveSection(index)}
              >
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">{step.number}</div>
                    <div className="step-icon">{step.icon}</div>
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>

                {index < howItWorksSteps.length - 1 && (
                  <div className="step-connector">
                    <svg width="40" height="40" viewBox="0 0 40 40">
                      <path
                        d="M20 0 L20 40 M10 30 L20 40 L30 30"
                        stroke="rgba(100, 149, 237, 0.5)"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <div className="cta-content">
            <h3>Ready to Explore the Cosmos?</h3>
            <p>
              Get real-time alerts for visible satellite passes in your area
            </p>
          </div>
          <CheckoutButton plan="premium_monthly" customClass="cosmic-button" />
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
